import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, PayloadAction } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { ControlTowerScoreTable, defaultValue } from 'app/shared/model/control-tower-score-table.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { toRFC3339UTC } from 'app/shared/util/date-utils';

const initialState: EntityState<ControlTowerScoreTable> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

export interface ControlTowerScoreTableProps {
  companyId?: number | null;
  periodicType: PeriodicType;
  dateStart: string;
  dateEnd: string;
  page?: number | null;
  size?: number | null;
  sort?: string | null;
  search?: string | null;
  vehicleCategory?: string | null;
}

// Actions

export const getControlTowerScoreAllVehicles = createAsyncThunk(
  'asset-classification/fetch_control_tower_score_table',
  async ({ companyId, periodicType, dateStart, dateEnd, page, size, sort, search, vehicleCategory }: ControlTowerScoreTableProps) => {
    const queryParams = new URLSearchParams();

    if (periodicType === PeriodicType.CUSTOM) {
      if (dateStart && dateEnd) {
        queryParams.append('dateStart', toRFC3339UTC(dateStart));
        queryParams.append('dateEnd', toRFC3339UTC(dateEnd));
      }
    }

    if (page !== null) {
      queryParams.append('page', page.toString());
    }

    if (size !== null) {
      queryParams.append('size', size.toString());
    }

    if (sort !== null) {
      queryParams.append('sort', sort);
    }

    if (search && search.trim() !== '') {
      queryParams.append('searchLicensePlate', search.trim());
    }

    if (companyId !== null) {
      queryParams.append('searchCompanyId', companyId.toString());
    }

    if (vehicleCategory !== null) {
      queryParams.append('categories', vehicleCategory);
    }

    const API_URL = `api/control-tower/score/all-vehicles/${periodicType}/?${queryParams.toString()}`;

    const response = await axios.get<ControlTowerScoreTable>(API_URL);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const controlTowerScoreTableSlice = createSlice({
  name: 'controlTowerScoreTable',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getControlTowerScoreAllVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })
      .addCase(getControlTowerScoreAllVehicles.pending, state => {
        state.loading = true;
      })
      .addCase(getControlTowerScoreAllVehicles.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset } = controlTowerScoreTableSlice.actions;

export default controlTowerScoreTableSlice.reducer;
